import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Mail } from "lucide-react";
import { readConsent } from "@/utils/consent";
import { getMetaCookies, trackLead, trackQualifiedLead, genEventId, setPixelUserIdentity } from "@/utils/pixel";
import { setUserToken } from "@/utils/userAuth";

// Note: keep `Dialog` import unused-safe — not used but consistent with project style
void Dialog;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ImpostaPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null); // {name, email}
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState("");

  const [form, setForm] = useState({ password: "", password2: "" });
  const [status, setStatus] = useState("idle"); // idle|loading|success|error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setTokenError("Link non valido — manca il token."); setLoading(false); return; }
    axios.get(`${API}/users/recover-info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => {
        const detail = err?.response?.data?.detail || "Link non valido o scaduto.";
        setTokenError(typeof detail === "string" ? detail : "Link non valido o scaduto.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError("La password deve essere di almeno 8 caratteri."); setStatus("error"); return; }
    if (form.password !== form.password2) { setError("Le due password non coincidono."); setStatus("error"); return; }
    setStatus("loading"); setError("");
    const meta = getMetaCookies();
    const consent = readConsent();
    try {
      const { data } = await axios.post(`${API}/users/complete-registration`, {
        token,
        password: form.password,
        fbp: meta.fbp, fbc: meta.fbc,
        landing_page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      setUserToken(data.access_token, true);
      // Propagate user identity to Pixel BEFORE firing Lead/QualifiedLead so
      // those events carry `external_id` like the server-side CAPI counterpart.
      try { if (data?.user?.id) setPixelUserIdentity(data.user.id); } catch { /* ignore */ }
      // Fire client-side Lead + QualifiedLead Pixel events (deduped server-side)
      if (consent.marketing) {
        try { trackLead(genEventId()); } catch { /* ignore */ }
        try {
          if (data?.user?.id) {
            trackQualifiedLead(`qualifiedlead_${data.user.id}`, { value: 2, currency: "EUR" });
          }
        } catch { /* ignore */ }
      }
      setStatus("success");
      setTimeout(() => navigate("/corso"), 700);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore nel salvataggio password.";
      setError(typeof detail === "string" ? detail : "Errore.");
      setStatus("error");
    }
  };

  if (loading) {
    return (
      <FullScreen>
        <Loader2 className="animate-spin mx-auto mb-4" size={28} style={{ color: "var(--np-secondary)" }} />
        <p className="text-sm" style={{ color: "var(--np-muted)" }}>Verifica del link in corso…</p>
      </FullScreen>
    );
  }

  if (tokenError) {
    return (
      <FullScreen>
        <AlertTriangle className="mx-auto mb-3" size={36} style={{ color: "#d95e39" }} />
        <h1 className="text-xl mb-2" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>
          Link non valido
        </h1>
        <p className="text-sm mb-5" style={{ color: "var(--np-muted)" }}>{tokenError}</p>
        <Link to="/login" data-testid="recover-fallback-login" className="cta-btn inline-flex">
          Vai al login
        </Link>
      </FullScreen>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-10" style={{ background: "var(--np-tertiary)" }}>
      <div data-testid="recover-password-card" className="max-w-md w-full bg-white rounded-2xl shadow-[0_30px_80px_-30px_rgba(26,42,58,0.35)] overflow-hidden">
        <div className="h-2 bg-tension" />
        <div className="px-7 pt-7 pb-8">
          <h1 className="text-2xl mb-1" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>
            {info?.is_reset ? "Reimposta la password" : "Imposta la tua password"}
          </h1>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: "var(--np-muted)" }}>
            {info?.is_reset ? "Inserisci la nuova password per il tuo account." : "Ultimo passo per accedere al corso."}
          </p>

          <div className="rounded-lg bg-[var(--np-quaternary)] border border-[#dbe3ec] px-3.5 py-2.5 mb-5 text-sm">
            <p className="text-xs uppercase tracking-wide font-semibold mb-1" style={{ color: "var(--np-primary)" }}>
              {info?.is_reset ? "Stai reimpostando la password per" : "Stai impostando la password per"}
            </p>
            <p className="font-medium" style={{ color: "var(--np-primary)" }}>{info?.name || "—"}</p>
            <p className="inline-flex items-center gap-1.5 text-xs mt-0.5" style={{ color: "var(--np-muted)" }}>
              <Mail size={12} /> {info?.email}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="recover-password-form">
            <Field id="password" type="password" label="Password (min. 8 caratteri)"
              testId="recover-password-input"
              value={form.password}
              onChange={(v) => { setForm({ ...form, password: v }); if (status === "error") setStatus("idle"); }}
              required minLength={8} placeholder="Minimo 8 caratteri" />
            <Field id="password2" type="password" label="Ripeti la password"
              testId="recover-password-confirm"
              value={form.password2}
              onChange={(v) => { setForm({ ...form, password2: v }); if (status === "error") setStatus("idle"); }}
              required minLength={8} placeholder="Reinserisci la password" />

            {form.password && form.password2 && form.password !== form.password2 && status !== "error" && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Le due password non coincidono.
              </p>
            )}
            {status === "error" && (
              <p data-testid="recover-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <button type="submit" data-testid="recover-password-submit" disabled={status === "loading"}
              className="cta-btn w-full mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {status === "loading" ? (<><Loader2 className="animate-spin" size={18} /> Salvo…</>)
                : status === "success" ? (<><CheckCircle2 size={18} /> Reindirizzo…</>)
                : (<>Crea account e vai al corso</>)}
            </button>

            <div className="flex items-start gap-2 pt-1">
              <ShieldCheck size={14} className="mt-0.5" style={{ color: "var(--np-muted)" }} />
              <p className="text-[12px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
                La password ti serve per rientrare nel corso da qualsiasi dispositivo.
              </p>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

function Field({ id, label, testId, value, onChange, type = "text", required, minLength, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[13px] font-semibold tracking-wide uppercase" style={{ color: "var(--np-primary)" }}>{label}</Label>
      <Input id={id} data-testid={testId} type={type} required={required} minLength={minLength}
        value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="h-11 rounded-lg border-[#dbe3ec] focus-visible:ring-[var(--np-secondary)]" />
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <main className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--np-tertiary)" }}>
      <div className="max-w-md w-full bg-white rounded-2xl shadow-[0_30px_80px_-30px_rgba(26,42,58,0.35)] overflow-hidden text-center">
        <div className="h-2 bg-tension" />
        <div className="p-8">{children}</div>
      </div>
    </main>
  );
}
